/**
 * GroupsInput — pole „Grupy" na formatce kartoteki (kontrahenci, pracownicy).
 *
 * MODEL: grupa = klasyfikacja rekordu do FILTROWANIA listy, WIELE na rekord, wartości ze
 * słownika globalnego (`contractor_groups` / `employee_groups`). NIE wpływa na layout
 * formularza — tym steruje kategoria (jedna na rekord), osobny mechanizm.
 *
 * Dlaczego dedykowany komponent, a nie generyczny renderer pola: kolumna to `text[]` z kodami
 * słownika, a generyczny renderer formularza obsługuje tylko 12 typów ext-pól (dlatego pole
 * systemowe `groups` ma `showInForm: false` i wstawiamy je tutaj ręcznie — dokładnie jak
 * istniejące outliery `tags` / `pkd_codes` w zakładce Dane kontrahenta).
 *
 * Pozycje bierzemy przez `useDictionary` (warstwa scalona tenanta: globalne + rozszerzenia
 * tenanta minus ukryte) i pokazujemy TYLKO wybieralne (`activeItems`) — z jednym wyjątkiem:
 * kod już zapisany na rekordzie, którego nie ma w bieżącym zbiorze wybieralnym (pozycja
 * ukryta/dezaktywowana po zapisie), zostaje pokazany jako chip, żeby edycja rekordu go nie
 * zjadła po cichu. Backend trzyma tę samą semantykę (waliduje tylko kody DOPISANE).
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { MultiSelect, type MultiSelectOption } from '@/components/MultiSelect';
import { useDictionary } from '@/hooks/useDictionary';

export interface GroupsInputProps {
  /** Kod słownika grup kartoteki, np. `contractor_groups`. */
  dictionaryCode: string;
  /** Kody wybranych grup (controlled). */
  value: ReadonlyArray<string>;
  onChange: (groups: string[]) => void;
  disabled?: boolean;
  'data-testid'?: string;
}

export function GroupsInput({
  dictionaryCode,
  value,
  onChange,
  disabled,
  'data-testid': testId = 'groups-input',
}: GroupsInputProps) {
  const { t } = useTranslation();
  const { activeItems, itemsByCode, isLoading } = useDictionary(dictionaryCode);

  const options = useMemo<MultiSelectOption[]>(() => {
    const opts: MultiSelectOption[] = activeItems.map((item) => ({
      value: item.code,
      label: item.label,
      color: item.color,
    }));
    // Kody zapisane na rekordzie, których nie ma już w zbiorze wybieralnym — dokładamy na
    // koniec, żeby chip nie zniknął (a wraz z nim przypisanie) przy edycji rekordu.
    const selectable = new Set(activeItems.map((i) => i.code));
    for (const code of value) {
      if (selectable.has(code)) continue;
      const known = itemsByCode.get(code);
      opts.push({
        value: code,
        label: known?.label ?? code,
        color: known?.color ?? null,
        description: t('tenant.groups.input.inactiveOption'),
      });
    }
    return opts;
  }, [activeItems, itemsByCode, value, t]);

  return (
    <MultiSelect
      value={value}
      onChange={onChange}
      options={options}
      disabled={disabled || isLoading}
      placeholder={t('tenant.groups.input.placeholder')}
      data-testid={testId}
    />
  );
}
